/* eslint-disable react-hooks/exhaustive-deps */
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AttachmentIcon from '@mui/icons-material/Attachment';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DescriptionIcon from '@mui/icons-material/Description';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
    Alert, Box, Button, FormControl, InputLabel, Select, MenuItem, Grid, Paper,
    Snackbar, Table, TableBody, TableCell, TableHead, TableRow, TextField,
    Typography, IconButton, Stack, Divider, TableContainer,
    Dialog, DialogTitle, DialogContent, DialogActions,
    List, ListItem, ListItemText, CircularProgress, Autocomplete, FormControlLabel,
    Checkbox, Tooltip, Tabs, Tab
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import axios from 'axios';
import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useParams } from 'react-router-dom';

// --- Components ---

/**
 * 🎯 Proper Sub-Component for Document Preview
 * This handles the Blob fetching and iframe rendering logic correctly.
 */
const DocumentPreviewer = ({ filepath }) => {
    const [blobUrl, setBlobUrl] = useState(null);
    const [loading, setLoading] = useState(false);

    const cleanPath = filepath ? filepath.replace(/\\/g, '/').replace(/^\/+/, '') : '';
    const fullUrl = `/api/${cleanPath}`;
    const fileExtension = cleanPath.split('.').pop().toLowerCase();

    useEffect(() => {
        if (fileExtension === 'pdf' && filepath) {
            setLoading(true);
            axios.get(fullUrl, { responseType: 'blob' })
                .then(response => {
                    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
                    setBlobUrl(url);
                    setLoading(false);
                })
                .catch(err => {
                    console.error("Blob fetch failed:", err);
                    setLoading(false);
                });
        }
        return () => {
            if (blobUrl) window.URL.revokeObjectURL(blobUrl);
        };
    }, [filepath, fileExtension, fullUrl]);

    if (!filepath) return null;

    if (fileExtension === 'pdf') {
        return (
            <Box sx={{ width: '100%', height: '100%', position: 'relative', bgcolor: '#fff' }}>
                {loading ? (
                    <Stack justifyContent="center" alignItems="center" sx={{ height: '100%' }}>
                        <CircularProgress />
                        <Typography sx={{ mt: 2, color: 'text.secondary' }}>Loading PDF Securely...</Typography>
                    </Stack>
                ) : (
                    <iframe
                        src={blobUrl || fullUrl}
                        width="100%"
                        height="100%"
                        style={{ border: 'none' }}
                        title="PDF Preview"
                    />
                )}
                <Button 
                    size="small" variant="contained" 
                    sx={{ position: 'absolute', bottom: 10, right: 10, opacity: 0.8 }}
                    onClick={() => window.open(fullUrl, '_blank')}
                >
                    Open Original
                </Button>
            </Box>
        );
    } 
    
    if (['jpg', 'jpeg', 'png', 'gif'].includes(fileExtension)) {
        return (
            <Box sx={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', bgcolor: '#fff', p: 1 }}>
                <img 
                    src={fullUrl} 
                    alt="preview" 
                    style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }} 
                    onError={(e) => { e.target.src = 'https://via.placeholder.com/400?text=Error+Loading+Image'; }}
                />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 4, textAlign: 'center', color: '#fff' }}>
            <Typography>Preview not available for .{fileExtension}</Typography>
            <Button variant="contained" sx={{ mt: 2 }} onClick={() => window.open(fullUrl, '_blank')}>
                Download File
            </Button>
        </Box>
    );
};

// --- Constants & Helpers ---
const CurrencyAdornment = () => (
    <Typography variant="body1" sx={{ mr: 1, color: 'text.secondary', fontWeight: 'bold' }}>R</Typography>
);

const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    return dateString.split('T')[0];
};

const LEDGER_CATEGORIES = [
    { id: 'Estate', label: 'Estate Account', color: '#1976d2' },
    { id: 'Investment', label: 'Investment Account', color: '#00796b' },
    { id: 'Inhouse', label: 'Inhouse Account (Internal Costs)', color: '#689f38' },
    { id: 'OBO', label: 'OBO Account (Linked Estates)', color: '#7b1fa2' },
    { id: 'Trading', label: 'Trading Account', color: '#e65100' }
];

const TRANSACTION_TYPES = [
    'Administration Cost',
    'Advanced (Rates & Taxes)',
    'Advanced Dividend',
    'Advertisement Cost',
    'Asset Insurance',
    'Auctioneer Cost',
    'Bank Charges',
    'Bond of Security',
    'Cash Found In Estate',
    'Contribution',
    'Courier',
    'Deposit',
    'Deposit on Immovable Property',
    'Enquiry Cost',
    'Fees Paid',
    'Final Dividend',
    'Guardians Fund',
    'Income Tax',
    'Interest Earned',
    'Investment',
    'Legal Cost',
    'Levies',
    'Master’s Fees',
    'Movables',
    'Occupational Rent',
    'Proceeds On Immovable Assets Sold',
    'Proceeds On Movable Assets Sold',
    'Rates & Taxes',
    'Refund',
    'Registered Letters',
    'Taxed Bill of Cost',
    'Transfer from Current Account',
    'Transfer from Inhouse Account',
    'Transfer from Investment Account',
    'Transfer to Current Account',
    'Transfer to Investment Account',
    'Traveling Cost',
    'Vat',
    'Waisted Cost'
];

const NPA_TRANSACTION_TYPES = [
    'Courier Costs',
    'Bank Charges',
    'Bond',
    'Interest Earned',
    'Admin Cost',
    'Copies',
    'Accommodation',
    'Security Services',
    'Fuel',
    'Toll Fees',
    'Asset Insurance',
    'Cash Found in Estate',
    'Sale of Moveable Assets',
    'Sale of Immovable Assets',
    'Bail Money',
    'Rentals Received',
    'Deposit on sale of Assets',
    'Deposit',
    'Fees Paid',
    'Legal Costs',
    'Maintenance',
    'Reimbursement Inhouse',
    'Rates & Taxes',
    'Sheriff Costs',
    'Trade Expenses',
    'Trade Income',
    'Travelling / Toll Fees'
];

// --- NPA-only "Inhouse Transactions" (internal costs of executing the order) ---
// Stored as normal Transaction rows tagged cashbookType='Inhouse', so they stay
// separate from the estate cashbook unless the Inhouse ledger is switched on.
const INHOUSE_TX_TYPES = [
    'TRAV - Travelling Expenses',
    'COPY - Document Copies/Printing',
    'POST - Postage/Courier Fees',
    'LODG - Accommodation/Lodging',
    'SERV - Service/Process Server Fees',
    'FUEL - Fuel/Mileage',
    'MISC - Miscellaneous Inhouse Expense',
];
const INHOUSE_SUMMARY_TYPES = ['TRAV', 'COPY', 'POST', 'LODG', 'SERV', 'FUEL', 'MISC'];

// A transaction is an "inhouse" one if it's tagged with the Inhouse ledger, or (for
// rows captured under the old Inhouse tab, before ledger tagging) if its type code is
// one of the inhouse expense codes.
const txTypeCode = (tx) => String(tx?.transactionType || '').split(' ')[0].trim().toUpperCase();
const isInhouseTx = (tx) => tx?.cashbookType === 'Inhouse' || INHOUSE_SUMMARY_TYPES.includes(txTypeCode(tx));

const InhouseTransactionsView = ({ legalMatterId, user }) => {
    const isGuest = user?.role === 'guest';
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [snack, setSnack] = useState({ open: false, msg: '', sev: 'success' });
    const [form, setForm] = useState({ transactionDate: '', transactionType: '', paymentReference: '', amount: '' });

    const fetchRows = async () => {
        if (!legalMatterId) return;
        setLoading(true);
        try {
            const res = await axios.get(`/api/transaction?legalMatterId=${legalMatterId}&limit=10000`);
            const data = res.data?.data || res.data || [];
            setRows((Array.isArray(data) ? data : []).filter(isInhouseTx));
        } catch (e) {
            setRows([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchRows(); }, [legalMatterId]);

    const totals = useMemo(() => {
        const t = {};
        let total = 0;
        rows.forEach(tx => {
            const code = String(tx.transactionType || '').split(' - ')[0].trim().toUpperCase();
            const amt = parseFloat(tx.debitTransaction || 0) - parseFloat(tx.creditTransaction || 0);
            total += amt;
            if (INHOUSE_SUMMARY_TYPES.includes(code)) t[code] = (t[code] || 0) + amt;
        });
        return { perType: t, balance: total };
    }, [rows]);

    const handleAdd = async () => {
        if (!form.transactionType || !form.amount) {
            setSnack({ open: true, msg: 'Expense Type and Amount are required.', sev: 'warning' });
            return;
        }
        try {
            await axios.post('/api/transaction', {
                legalMatterId,
                cashbookType: 'Inhouse',
                transactionType: form.transactionType,
                transactionDate: form.transactionDate || new Date().toISOString().split('T')[0],
                paymentReference: form.paymentReference,
                description: form.paymentReference,
                debitTransaction: parseFloat(form.amount || 0).toFixed(2),
                creditTransaction: '0.00',
            });
            setSnack({ open: true, msg: 'Internal expense saved.', sev: 'success' });
            setForm({ transactionDate: '', transactionType: '', paymentReference: '', amount: '' });
            fetchRows();
        } catch (e) {
            setSnack({ open: true, msg: 'Failed to save internal expense.', sev: 'error' });
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this internal expense?')) return;
        try {
            await axios.delete(`/api/transaction/${id}`);
            fetchRows();
        } catch (e) {
            setSnack({ open: true, msg: 'Failed to delete.', sev: 'error' });
        }
    };

    return (
        <Box sx={{ mt: 1 }}>
            <Alert severity="info" sx={{ mb: 3 }}>
                Track <strong>internal expenses</strong> incurred executing the order (travelling, copies, postage, service of documents, etc.). These are kept separate from the estate cashbook.
            </Alert>

            <Paper elevation={3} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>Add New Internal Expense</Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth size="small" type="date" label="Date" InputLabelProps={{ shrink: true }}
                            value={form.transactionDate} onChange={(e) => setForm(p => ({ ...p, transactionDate: e.target.value }))}
                            disabled={isGuest} inputProps={{ max: '9999-12-31' }} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth size="small" disabled={isGuest}>
                            <InputLabel>Expense Type</InputLabel>
                            <Select label="Expense Type" value={form.transactionType} onChange={(e) => setForm(p => ({ ...p, transactionType: e.target.value }))}>
                                {INHOUSE_TX_TYPES.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth size="small" label="Reference" value={form.paymentReference}
                            onChange={(e) => setForm(p => ({ ...p, paymentReference: e.target.value }))} disabled={isGuest} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth size="small" type="number" label="Amount" value={form.amount}
                            onChange={(e) => setForm(p => ({ ...p, amount: e.target.value }))} disabled={isGuest}
                            InputProps={{ startAdornment: <CurrencyAdornment /> }} />
                    </Grid>
                    <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button variant="contained" onClick={handleAdd} disabled={isGuest}>Save Internal Expense</Button>
                    </Grid>
                </Grid>
            </Paper>

            <Paper elevation={3} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>Inhouse Expenses Summary</Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={4}>
                        <TextField fullWidth size="small" label="Total Inhouse Balance" disabled InputLabelProps={{ shrink: true }}
                            value={totals.balance.toFixed(2)}
                            InputProps={{ startAdornment: <CurrencyAdornment />, sx: { fontWeight: 'bold' } }} />
                    </Grid>
                    {INHOUSE_SUMMARY_TYPES.map(code => (
                        <Grid item xs={12} sm={6} md={4} key={code}>
                            <TextField fullWidth size="small" label={`Total ${code}`} disabled InputLabelProps={{ shrink: true }}
                                value={(totals.perType[code] || 0).toFixed(2)}
                                InputProps={{ startAdornment: <CurrencyAdornment /> }} />
                        </Grid>
                    ))}
                </Grid>
            </Paper>

            <Paper elevation={3} sx={{ p: 2, borderRadius: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Recorded Internal Expenses</Typography>
                    <Button size="small" variant="outlined" startIcon={<FileDownloadIcon />}
                        onClick={() => window.open(`/api/cashbook-summary/download-pdf?matterId=${legalMatterId}&cashbookType=Inhouse`, '_blank')}>
                        Print PDF
                    </Button>
                </Stack>
                <TableContainer>
                    <Table size="small">
                        <TableHead sx={{ bgcolor: '#eee' }}>
                            <TableRow>
                                <TableCell>Date</TableCell>
                                <TableCell>Type</TableCell>
                                <TableCell>Reference</TableCell>
                                <TableCell align="right">Amount</TableCell>
                                <TableCell align="center">Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow><TableCell colSpan={5} align="center" sx={{ py: 3 }}><CircularProgress size={24} /></TableCell></TableRow>
                            ) : rows.length === 0 ? (
                                <TableRow><TableCell colSpan={5} align="center" sx={{ py: 3, fontStyle: 'italic', color: 'text.secondary' }}>No internal expenses recorded.</TableCell></TableRow>
                            ) : (
                                [...rows].sort((a, b) => new Date(b.transactionDate) - new Date(a.transactionDate)).map(tx => (
                                    <TableRow key={tx.id} hover>
                                        <TableCell>{formatDateForInput(tx.transactionDate)}</TableCell>
                                        <TableCell>{tx.transactionType}</TableCell>
                                        <TableCell>{tx.paymentReference || '-'}</TableCell>
                                        <TableCell align="right">R {parseFloat(tx.debitTransaction || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</TableCell>
                                        <TableCell align="center">
                                            <IconButton size="small" color="error" onClick={() => handleDelete(tx.id)} disabled={isGuest}><DeleteIcon fontSize="inherit" /></IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack(s => ({ ...s, open: false }))}>
                <Alert severity={snack.sev} sx={{ width: '100%' }}>{snack.msg}</Alert>
            </Snackbar>
        </Box>
    );
};

const CashbookTransactionsTab = ({ matter, onSummaryUpdate }) => {
    const isNPAMatter = matter?.estateClassification === 'NPA' || matter?.estateClassification === 'Npa Matters' || matter?.estateClassification === 'Curator Appointments';
    const transactionTypeOptions = isNPAMatter ? NPA_TRANSACTION_TYPES : TRANSACTION_TYPES;

    const { id } = useParams();
    const modalFileInputRef = useRef(null);

    // --- State ---
    const [activeView, setActiveView] = useState(0); // 0 = Main Cashbook, 1 = Inhouse (NPA only)
    const [transactions, setTransactions] = useState([]);
    const [matterDocuments, setMatterDocuments] = useState([]);
    const [showLedgerDialog, setShowLedgerDialog] = useState(false);
    const [showDocPicker, setShowDocPicker] = useState(false);
    const [draggedTxId, setDraggedTxId] = useState(null); // 🎯 9.3: drag-and-drop reorder within a date
    const [expandedSections, setExpandedSections] = useState({ Estate: true });
    const [isUploading, setIsUploading] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    
    // --- Form State ---
    const [transactionType, setTransactionType] = useState('');
    const [transactionDate, setTransactionDate] = useState('');
    const [paymentReference, setPaymentReference] = useState('');
    const [statementNumber, setStatementNumber] = useState('');
    const [transactionParty, setTransactionParty] = useState('');
    const [description, setDescription] = useState('');
    const [debitTransaction, setDebitTransaction] = useState('');
    const [creditTransaction, setCreditTransaction] = useState('');
    const [selectedTransactionId, setSelectedTransactionId] = useState(null);
    const [linkedDocUrl, setLinkedDocUrl] = useState(null);
    const [bankAccountId, setBankAccountId] = useState(''); // 🎯 NEW: State for selected bank account
    const [vatApplicable, setVatApplicable] = useState(false); // VAT state

    // --- Bank Accounts State ---
    const [bankAccounts, setBankAccounts] = useState(matter?.bankAccounts || []);
    const [editingBankAccountId, setEditingBankAccountId] = useState(null);
    const [bankAccountForm, setBankAccountForm] = useState({
        bankName: '', bankAccountNo: '', bankAccountOpenedDate: '', bankAccountClosedDate: '',
        bankAccountConfirmationUrl: null, zeroBalanceStatementUrl: null
    });
    const [activeLedgers, setActiveLedgers] = useState(matter?.activeLedgers || ['Estate']);

    // State contextual flag to track what state property the document picker should update
    const [docPickerTarget, setDocPickerTarget] = useState('transaction'); // 'transaction' | 'confirmationLetter' | 'zeroBalance'

    const [user, setUser] = useState(null);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');

    const legalMatterId = (id && id !== 'undefined') ? id : (matter?.id || '');

    // --- Auto-Fill Feature Cache Lookup ---
    const historicalDataMap = useMemo(() => {
        const cache = {};
        for (let i = transactions.length - 1; i >= 0; i--) {
            const tx = transactions[i];
            if (tx.transactionType && !cache[tx.transactionType]) {
                cache[tx.transactionType] = {
                    description: tx.description || '',
                    paymentReference: tx.paymentReference || '',
                    transactionParty: tx.transactionParty || ''
                };
            }
        }
        return cache;
    }, [transactions]);

    useEffect(() => {
        if (!selectedTransactionId && transactionType && historicalDataMap[transactionType]) {
            const history = historicalDataMap[transactionType];
            if (!description) setDescription(history.description);
            if (!paymentReference) setPaymentReference(history.paymentReference);
            if (!transactionParty) setTransactionParty(history.transactionParty);
        }
    }, [transactionType, historicalDataMap, selectedTransactionId]);

    // --- Summaries via useMemo ---
    const summaryBankCharges = useMemo(() => {
        return transactions
            .filter(tx => {
                const type = (tx.transactionType || '').trim().toLowerCase();
                return type === 'bank charges' || type === 'bank charge';
            })
            .reduce((sum, tx) => {
                const debit = parseFloat(tx.debitTransaction || 0);
                const credit = parseFloat(tx.creditTransaction || 0);
                return sum + (debit - credit);
            }, 0);
    }, [transactions]);

    const summaryInterest = useMemo(() => {
        return transactions
            .filter(tx => {
                const type = (tx.transactionType || '').trim().toLowerCase();
                return ['interest earned', 'interest received', 'interest'].includes(type);
            })
            .reduce((sum, tx) => {
                const debit = parseFloat(tx.debitTransaction || 0);
                const credit = parseFloat(tx.creditTransaction || 0);
                return sum + (credit - debit);
            }, 0);
    }, [transactions]);

    useEffect(() => {
        if (onSummaryUpdate && transactions.length > 0) {
            onSummaryUpdate({ summaryInterest, summaryBankCharges });
        }
    }, [summaryInterest, summaryBankCharges]);

    // --- Logic ---
    const normalize = (path) => path ? path.replace(/\\/g, '/').toLowerCase() : "";

    const fetchTransactions = async () => {
        if (!legalMatterId) return;
        try {
            const res = await axios.get(`/api/transaction?legalMatterId=${legalMatterId}&limit=10000`);
            const data = res.data?.data || res.data || [];
            setTransactions(Array.isArray(data) ? data : []);
        } catch (error) { setTransactions([]); }
    };

    // 🎯 9.3: Reordering transactions within the same date via drag-and-drop.
    // dateGroupTxs must be the transactions sharing draggedTx's date, in current display order.
    const handleReorderDrop = async (draggedTx, targetTx, dateGroupTxs) => {
        if (!draggedTx || !targetTx || draggedTx.id === targetTx.id) return;
        if (draggedTx.transactionDate !== targetTx.transactionDate) return; // only within the same date

        const ids = dateGroupTxs.map(t => t.id);
        const fromIndex = ids.indexOf(draggedTx.id);
        const toIndex = ids.indexOf(targetTx.id);
        if (fromIndex === -1 || toIndex === -1) return;

        const reordered = [...dateGroupTxs];
        const [moved] = reordered.splice(fromIndex, 1);
        reordered.splice(toIndex, 0, moved);

        const updates = reordered.map((t, idx) => ({ id: t.id, sortOrder: (idx + 1) * 10 }));

        // Optimistic local update so the row order reflects the drop immediately.
        setTransactions(prev => prev.map(t => {
            const update = updates.find(u => u.id === t.id);
            return update ? { ...t, sortOrder: update.sortOrder } : t;
        }));

        try {
            await axios.put('/api/transaction/reorder', { updates });
        } catch (e) {
            console.error("Failed to save transaction reorder", e);
            fetchTransactions(); // revert to server truth on failure
        }
    };

    const fetchDocs = async () => {
        if (!legalMatterId || legalMatterId === 'undefined') return;
        try {
            const docsRes = await axios.get(`/api/cashbook/available-documents/${legalMatterId}`);
            const allDocs = Array.isArray(docsRes.data) ? docsRes.data : [];

            let currentMatchingTarget = null;
            if (docPickerTarget === 'transaction') currentMatchingTarget = linkedDocUrl;
            else if (docPickerTarget === 'confirmationLetter') currentMatchingTarget = bankAccountForm.bankAccountConfirmationUrl;
            else if (docPickerTarget === 'zeroBalance') currentMatchingTarget = bankAccountForm.zeroBalanceStatementUrl;

            if (currentMatchingTarget) {
                const targetPath = normalize(currentMatchingTarget);
                const linked = allDocs.filter(doc => normalize(doc.filepath) === targetPath);
                if (linked.length > 0) {
                    setMatterDocuments(linked);
                    return; 
                }
            }
            setMatterDocuments(allDocs);
        } catch (err) {
            setMatterDocuments([]); 
        }
    };

    useEffect(() => {
        if (legalMatterId) {
            fetchTransactions();
            axios.get('/api/user', { withCredentials: true }).then(r => setUser(r.data));
        }
    }, [legalMatterId]);

    useEffect(() => {
        if (matter?.activeLedgers) setActiveLedgers(matter.activeLedgers);
    }, [matter?.activeLedgers]);

    useEffect(() => {
        if (matter?.bankAccounts) setBankAccounts(matter.bankAccounts);
    }, [matter?.bankAccounts]);

    useEffect(() => {
        if (legalMatterId && showDocPicker) fetchDocs();
    }, [selectedTransactionId, linkedDocUrl, bankAccountForm.bankAccountConfirmationUrl, bankAccountForm.zeroBalanceStatementUrl, showDocPicker, legalMatterId, docPickerTarget]);

    // --- Actions ---
    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!isDragging) setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDrop = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        const file = e.dataTransfer.files[0];
        if (file) {
            await handleFileUploadInModal({ target: { files: [file] } });
        }
    };

    const handleEditClick = (tx) => {
        setDocPickerTarget('transaction');
        setSelectedTransactionId(tx.id);
        setTransactionDate(formatDateForInput(tx.transactionDate));
        setTransactionType(tx.transactionType || '');
        setPaymentReference(tx.paymentReference || '');
        setStatementNumber(tx.statementNumber || '');
        setTransactionParty(tx.transactionParty || '');
        setDescription(tx.description || '');
        setDebitTransaction(tx.debitTransaction || '');
        setBankAccountId(tx.bankAccountId || ''); // 🎯 NEW: Load bank account ID
        setCreditTransaction(tx.creditTransaction || '');
        setLinkedDocUrl(tx.documentUrl || tx.filepath || null); 
        setVatApplicable(tx.vatApplicable || false); // Load VAT state
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleFileUploadInModal = async (event) => {
        const file = event.target.files[0];
        if (!file || !user?.emailAddress) return;
        setIsUploading(true);
        const formData = new FormData();
        formData.append('documents', file);
        formData.append('folder', 'Cashbook');
        try {
            await axios.post(`/api/legal-matter/${legalMatterId}/uploads`, formData, {
                headers: { 'x-user-email': user.emailAddress, 'x-user-role': user.role },
                withCredentials: true
            });
            setSnackbarMessage("Uploaded successfully.");
            setOpenSnackbar(true);
            await fetchDocs(); 
        } catch (error) { setSnackbarMessage("Upload failed."); setOpenSnackbar(true); }
        finally { setIsUploading(false); }
    };

    const handleFinalPost = async (ledgerType) => {
        setShowLedgerDialog(false);

        // Validate required fields: only transactionType is mandatory for posting
        if (!transactionType) {
            setSnackbarMessage("Transaction Type is required.");
            setSnackbarSeverity("warning");
            setOpenSnackbar(true);
            return;
        }
        const payload = {
            transactionDate, paymentReference, statementNumber, legalMatterId,
            transactionType, description, cashbookType: ledgerType, transactionParty,
            debitTransaction: parseFloat(debitTransaction || 0).toFixed(2), vatApplicable, // Save VAT state
            creditTransaction: parseFloat(creditTransaction || 0).toFixed(2),
            bankAccountId: bankAccountId || null, // 🎯 NEW: Include bank account ID in payload
            documentUrl: linkedDocUrl,
        };
        try {
            if (selectedTransactionId) await axios.put(`/api/transaction/${selectedTransactionId}`, payload);
            else await axios.post('/api/transaction', payload);
            setSnackbarMessage("Saved."); setOpenSnackbar(true); resetForm(); fetchTransactions();
        } catch (error) { setSnackbarMessage("Error saving."); setOpenSnackbar(true); }
    };

    const resetForm = () => {
        setTransactionType(''); setTransactionDate(''); setPaymentReference('');
        setStatementNumber(''); setTransactionParty(''); setDescription(''); setDebitTransaction(''); 
         setCreditTransaction(''); setSelectedTransactionId(null); setLinkedDocUrl(null); setVatApplicable(false); setBankAccountId(''); // Reset VAT state & Bank Account
    };


    const handleSaveActiveLedgers = async (newLedgers) => {
        try {
            await axios.put(`/api/legal-matter/${legalMatterId}`, { activeLedgers: newLedgers });
            setSnackbarMessage("Active ledgers updated.");
            setOpenSnackbar(true);
            if (onSummaryUpdate) onSummaryUpdate({ activeLedgers: newLedgers });
        } catch (error) {
            setSnackbarMessage("Error updating ledgers.");
            setOpenSnackbar(true);
        }
    };

    const resetBankAccountForm = () => {
        setEditingBankAccountId(null);
        setBankAccountForm({ 
            bankName: '', bankAccountNo: '', bankAccountOpenedDate: '', bankAccountClosedDate: '',
            bankAccountConfirmationUrl: null, zeroBalanceStatementUrl: null
        });
    };

    const saveBankAccounts = async (updatedList) => {
        try {
            await axios.put(`/api/legal-matter/${legalMatterId}`, { bankAccounts: updatedList });
            setSnackbarMessage("Bank accounts updated successfully.");
            setOpenSnackbar(true);
            if (onSummaryUpdate) onSummaryUpdate({ bankAccounts: updatedList });
        } catch (error) {
            setSnackbarMessage("Error updating bank accounts.");
            setOpenSnackbar(true);
        }
    };

    const handleSaveBankAccount = () => {
        if (!bankAccountForm.bankName || !bankAccountForm.bankAccountNo) return;
        let updatedList = [...bankAccounts];
        if (editingBankAccountId) {
            updatedList = updatedList.map(b => b.id === editingBankAccountId ? { ...bankAccountForm, id: editingBankAccountId } : b);
        } else {
            updatedList.push({ ...bankAccountForm, id: -Math.floor(Math.random() * 1000000) });
        }
        setBankAccounts(updatedList);
        saveBankAccounts(updatedList);
        resetBankAccountForm();
        fetchTransactions(); // Re-fetch transactions to update linked names
    };

    const handleDeleteBankAccount = (id) => {
        if (window.confirm("Are you sure you want to delete this bank account?")) {
            const updatedList = bankAccounts.filter(b => b.id !== id);
            setBankAccounts(updatedList);
            saveBankAccounts(updatedList);
        }
    };

    // Helper triggered inside picker modal list item selects
    const handlePickerSelection = (filepath) => {
        if (docPickerTarget === 'transaction') {
            setLinkedDocUrl(filepath);
        } else if (docPickerTarget === 'confirmationLetter') {
            setBankAccountForm(prev => ({ ...prev, bankAccountConfirmationUrl: filepath }));
        } else if (docPickerTarget === 'zeroBalance') {
            setBankAccountForm(prev => ({ ...prev, zeroBalanceStatementUrl: filepath }));
        }
    };

    const getPickerActivePathValue = () => {
        if (docPickerTarget === 'transaction') return linkedDocUrl;
        if (docPickerTarget === 'confirmationLetter') return bankAccountForm.bankAccountConfirmationUrl;
        if (docPickerTarget === 'zeroBalance') return bankAccountForm.zeroBalanceStatementUrl;
        return null;
    };

    return (
        <Grid container spacing={3}>
            {isNPAMatter && (
                <Grid item xs={12}>
                    <Paper elevation={2} sx={{ borderRadius: 2 }}>
                        <Tabs value={activeView} onChange={(e, v) => setActiveView(v)} variant="fullWidth">
                            <Tab label="Main Cashbook" value={0} />
                            <Tab label="Inhouse Transactions" value={1} />
                        </Tabs>
                    </Paper>
                </Grid>
            )}

            {isNPAMatter && activeView === 1 && (
                <Grid item xs={12}>
                    <InhouseTransactionsView legalMatterId={legalMatterId} user={user} />
                </Grid>
            )}

            {(!isNPAMatter || activeView === 0) && (<>
            {/* 1. ENTRY FORM */}
            <Grid item xs={12}>
                <Paper elevation={3} sx={{ p: 3, borderTop: `6px solid ${selectedTransactionId ? '#ed6c02' : '#1976d2'}`, borderRadius: 2 }}>
                    <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>{selectedTransactionId ? `Edit: ${paymentReference}` : 'Capture Transaction'}</Typography>
                    </Stack>
                    <Grid container spacing={2}>
                        {/* 🎯 NEW: Bank Account Selector */}
                        <Grid item xs={12} sm={3}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Allocate to Bank Account</InputLabel>
                                <Select value={bankAccountId} label="Allocate to Bank Account" onChange={(e) => setBankAccountId(e.target.value)}>
                                    <MenuItem value=""><em>None</em></MenuItem>
                                    {bankAccounts.map((acc) => (<MenuItem key={acc.id} value={acc.id}>{acc.bankName} - (...{acc.bankAccountNo.slice(-4)})</MenuItem>))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={3}><TextField fullWidth size="small" type="date" value={transactionDate} onChange={(e) => setTransactionDate(e.target.value)} InputLabelProps={{ shrink: true }} label="Date" inputProps={{ max: '9999-12-31' }}/></Grid>
                        <Grid item xs={12} sm={3}>
                            <Autocomplete
                                freeSolo
                                options={transactionTypeOptions}
                                value={transactionType || ''}
                                onChange={(e, newValue) => setTransactionType(newValue || '')}
                                onInputChange={(e, newInputValue) => setTransactionType(newInputValue || '')}
                                renderInput={(params) => (
                                    <TextField {...params} label="Type" size="small" fullWidth placeholder="Select or type..." required />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} sm={3}><TextField fullWidth size="small" label="Reference" value={paymentReference} onChange={(e) => setPaymentReference(e.target.value)} /></Grid>
                        <Grid item xs={12} sm={3}><TextField fullWidth size="small" label="Statement #" value={statementNumber} onChange={(e) => setStatementNumber(e.target.value)} /></Grid>
                        <Grid item xs={12} sm={4}><TextField fullWidth size="small" label="Paid To / Received From" placeholder="Enter party name..." value={transactionParty} onChange={(e) => setTransactionParty(e.target.value)} /></Grid>
                        <Grid item xs={12} sm={3}><TextField fullWidth size="small" label="Description" value={description} onChange={(e) => setDescription(e.target.value)} /></Grid>
                        <Grid item xs={12} sm={2} sx={{ display: 'flex', alignItems: 'center' }}>
                            <Tooltip title="Check if VAT is applicable to this transaction invoice.">
                                <FormControlLabel
                                    control={<Checkbox checked={vatApplicable} onChange={(e) => setVatApplicable(e.target.checked)} name="vatApplicable" />}
                                    label="VAT"
                                />
                            </Tooltip>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <Button 
                                fullWidth variant="outlined" 
                                color={linkedDocUrl ? "success" : "primary"} 
                                startIcon={selectedTransactionId && linkedDocUrl ? <VisibilityIcon /> : <AttachmentIcon />} 
                                onClick={() => { setDocPickerTarget('transaction'); setShowDocPicker(true); }}
                                sx={{ height: '40px' }}
                            >
                                {linkedDocUrl ? (selectedTransactionId ? "View Document" : "Doc Attached") : "Link Document"}
                            </Button>
                        </Grid>
                        <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Debit" type="number" value={debitTransaction} onChange={(e) => setDebitTransaction(e.target.value)} InputProps={{ startAdornment: <CurrencyAdornment /> }} /></Grid>
                        <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Credit" type="number" value={creditTransaction} onChange={(e) => setCreditTransaction(e.target.value)} InputProps={{ startAdornment: <CurrencyAdornment /> }} /></Grid>
                        <Grid item xs={12} sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                            {selectedTransactionId && <Button variant="text" color="error" onClick={resetForm}>Cancel</Button>}
                            <Button variant="outlined" onClick={resetForm}>Clear</Button>
                            <Button variant="contained" color={selectedTransactionId ? "warning" : "primary"} onClick={() => setShowLedgerDialog(true)}>
                                {selectedTransactionId ? 'Save Changes' : 'Post Transaction'}
                            </Button>
                        </Grid>
                    </Grid>
                </Paper>
            </Grid>

            {/* 🎯 SIDE-BY-SIDE PREVIEW MODAL */}
            <Dialog open={showDocPicker} onClose={() => setShowDocPicker(false)} maxWidth="lg" fullWidth>
                <DialogTitle>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6">
                            Supporting Documents 
                            {docPickerTarget === 'confirmationLetter' && " (Select Bank Confirmation Letter)"}
                            {docPickerTarget === 'zeroBalance' && " (Select Zero Balance Statement)"}
                        </Typography>
                        <Box>
                            <input type="file" ref={modalFileInputRef} hidden onChange={handleFileUploadInModal} />
                            <Button variant="contained" size="small" startIcon={<CloudUploadIcon />} onClick={() => modalFileInputRef.current.click()} disabled={isUploading}>
                                {isUploading ? "Uploading..." : "Upload New"}
                            </Button>
                        </Box>
                    </Stack>
                </DialogTitle>
                <Divider />
                <DialogContent sx={{ p: 0, height: '600px', display: 'flex' }}>
                    {/* LEFT SIDE: List */}
                    <Box 
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        sx={{ 
                            width: '30%', 
                            borderRight: '1px solid #ddd', 
                            overflowY: 'auto', 
                            bgcolor: isDragging ? '#e3f2fd' : '#f9f9f9',
                            transition: 'background-color 0.2s'
                        }}
                    >
                        <List>
                            {matterDocuments.length === 0 ? (
                                <Typography align="center" sx={{ py: 4, color: 'text.secondary' }}>No documents found.</Typography>
                            ) : (
                                matterDocuments.map((doc) => {
                                    const docPath = normalize(doc.filepath || doc.fileUrl);
                                    const isSelected = normalize(getPickerActivePathValue()) === docPath;

                                    return (
                                        <ListItem 
                                            button 
                                            key={doc.id} 
                                            selected={isSelected}
                                            onClick={() => handlePickerSelection(doc.filepath)}
                                            sx={{ borderBottom: '1px solid #eee' }}
                                        >
                                            <ListItemText 
                                                primary={doc.fileName || doc.name} 
                                                secondary={`Date: ${formatDateForInput(doc.uploadDate)}`}
                                            />
                                        </ListItem>
                                    );
                                })
                            )}
                        </List>
                        {getPickerActivePathValue() && matterDocuments.length === 1 && (
                            <Box sx={{ p: 2, textAlign: 'center' }}>
                                <Button size="small" variant="text" onClick={() => { handlePickerSelection(null); fetchDocs(); }}>
                                    Show All Files
                                </Button>
                            </Box>
                        )}
                    </Box>

                    {/* RIGHT SIDE: Sub-Component Preview */}
                    <Box sx={{ width: '70%', height: '100%', bgcolor: '#525659' }}>
                        {getPickerActivePathValue() ? (
                            <DocumentPreviewer filepath={getPickerActivePathValue()} />
                        ) : (
                            <Stack justifyContent="center" alignItems="center" sx={{ height: '100%', color: '#fff' }}>
                                <VisibilityIcon sx={{ fontSize: 48, opacity: 0.5, mb: 1 }} />
                                <Typography>Select a document to preview</Typography>
                            </Stack>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowDocPicker(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* LEDGER TABLES */}
            <Grid item xs={12}>
                <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                    <Paper elevation={1} sx={{ p: 2, flex: 1, bgcolor: '#e3f2fd', borderLeft: '4px solid #1976d2' }}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 'bold' }}>Total Interest</Typography>
                        <Typography variant="h6">R {summaryInterest.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography>
                    </Paper>
                    <Paper elevation={1} sx={{ p: 2, flex: 1, bgcolor: '#ffebee', borderLeft: '4px solid #c2185b' }}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 'bold' }}>Total Bank Charges</Typography>
                        <Typography variant="h6">R {summaryBankCharges.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography>
                    </Paper>
                </Stack>

                {/* Active Ledgers Selector */}
                <Box sx={{ mb: 2, p: 2, bgcolor: '#fcfcfc', borderRadius: 1, border: '1px solid #eee' }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>Manage Applicable Accounts</Typography>
                    <FormControl size="small" fullWidth>
                        <InputLabel>Active Ledgers</InputLabel>
                        <Select
                            multiple
                            value={activeLedgers}
                            label="Active Ledgers"
                            onChange={(e) => {
                                const val = e.target.value;
                                setActiveLedgers(val);
                                handleSaveActiveLedgers(val);
                            }}
                            renderValue={(selected) => selected.map(id => LEDGER_CATEGORIES.find(c => c.id === id)?.label || id).join(', ')}
                        >
                            {LEDGER_CATEGORIES.map((cat) => (
                                <MenuItem key={cat.id} value={cat.id}>
                                    <Checkbox checked={activeLedgers.indexOf(cat.id) > -1} />
                                    <ListItemText primary={cat.label} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                <Paper elevation={3} sx={{ borderRadius: '8px', overflow: 'hidden' }}>
                    <TableContainer>
                        <Table size="small">
                            <TableHead sx={{ bgcolor: '#eee' }}>
                                <TableRow>
                                    <TableCell sx={{ width: 32 }} /> {/* 🎯 9.3: drag handle column */}
                                    <TableCell sx={{ width: 40 }}>Doc</TableCell>
                                    <TableCell>Bank Account</TableCell> {/* 🎯 NEW */}
                                    <TableCell>Date</TableCell>
                                    <TableCell>Type</TableCell>
                                    <TableCell>Reference</TableCell>
                                    <TableCell>Stmt#</TableCell>
                                    <TableCell>Description Summary</TableCell>
                                    <TableCell>VAT</TableCell>
                                    <TableCell align="right">Debit</TableCell>
                                    <TableCell align="right">Credit</TableCell>
                                    <TableCell align="right">Balance</TableCell>
                                    <TableCell align="center">Action</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {LEDGER_CATEGORIES.filter(cat => activeLedgers.includes(cat.id)).map((category) => {
                                    const categoryTxs = transactions.filter(tx => {
                                        // Inhouse expenses live in their own ledger / the Inhouse tab -
                                        // never let them fall through to the Estate bucket.
                                        if (isNPAMatter && isInhouseTx(tx) && category.id !== 'Inhouse') return false;
                                        return tx.cashbookType === category.id || (!tx.cashbookType && category.id === 'Estate');
                                    });
                                    let bal = 0;
                                    const processed = [...categoryTxs].sort((a, b) => new Date(a.transactionDate) - new Date(b.transactionDate) || ((a.sortOrder ?? 0) - (b.sortOrder ?? 0))).map(tx => {
                                        bal += (parseFloat(tx.creditTransaction || 0) - parseFloat(tx.debitTransaction || 0));
                                        return { ...tx, runningBalance: bal.toFixed(2) };
                                    });

                                    return (
                                        <React.Fragment key={category.id}>
                                            <TableRow onClick={() => setExpandedSections(p => ({...p, [category.id]: !p[category.id]}))} sx={{ bgcolor: '#f0f4f8', cursor: 'pointer' }}>
                                                <TableCell colSpan={12} sx={{ py: 1.5 }}>
                                                    <Stack direction="row" justifyContent="space-between">
                                                        <Stack direction="row" spacing={1} alignItems="center">
                                                            <ExpandMoreIcon sx={{ transform: expandedSections[category.id] ? 'rotate(0deg)' : 'rotate(-90deg)' }} />
                                                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: category.color }}>{category.label.toUpperCase()}</Typography>
                                                        </Stack>
                                                        <Stack direction="row" spacing={2} alignItems="center">
                                                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>R {bal.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography>
                                                            <Tooltip title={`Print / download the ${category.label} as a PDF`}>
                                                                <Button
                                                                    size="small"
                                                                    variant="outlined"
                                                                    startIcon={<FileDownloadIcon />}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        window.open(`/api/cashbook-summary/download-pdf?matterId=${legalMatterId}&cashbookType=${category.id}`, '_blank');
                                                                    }}
                                                                >
                                                                    Print PDF
                                                                </Button>
                                                            </Tooltip>
                                                        </Stack>
                                                    </Stack>
                                                </TableCell>
                                            </TableRow>
                                            {expandedSections[category.id] && processed.map((tx) => (
                                                <TableRow
                                                    key={tx.id}
                                                    hover
                                                    draggable={user?.role !== 'guest'}
                                                    onDragStart={(e) => {
                                                        // Firefox aborts the whole drag operation (no dragover/drop
                                                        // events at all) unless dataTransfer.setData() is called
                                                        // here - this was missing, which is why reordering silently
                                                        // did nothing.
                                                        e.dataTransfer.effectAllowed = 'move';
                                                        e.dataTransfer.setData('text/plain', String(tx.id));
                                                        setDraggedTxId(tx.id);
                                                    }}
                                                    onDragOver={(e) => {
                                                        // Must unconditionally preventDefault for the row to be a
                                                        // valid drop target at all (was conditional on draggedTxId,
                                                        // which is a state read racing the drag's own event timing).
                                                        e.preventDefault();
                                                        e.dataTransfer.dropEffect = 'move';
                                                    }}
                                                    onDrop={(e) => {
                                                        e.preventDefault();
                                                        const draggedId = draggedTxId || e.dataTransfer.getData('text/plain');
                                                        const draggedTx = processed.find(t => String(t.id) === String(draggedId));
                                                        const dateGroupTxs = processed.filter(t => t.transactionDate === tx.transactionDate);
                                                        handleReorderDrop(draggedTx, tx, dateGroupTxs);
                                                        setDraggedTxId(null);
                                                    }}
                                                    onDragEnd={() => setDraggedTxId(null)}
                                                    sx={{ opacity: draggedTxId === tx.id ? 0.4 : 1 }}
                                                >
                                                    {/* 🎯 9.3: Grip handle - reorder within the same date */}
                                                    <TableCell sx={{ py: 0.5, cursor: user?.role === 'guest' ? 'default' : 'grab' }}>
                                                        {user?.role !== 'guest' && (
                                                            <Tooltip title="Drag to reorder within this date">
                                                                <DragIndicatorIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                                                            </Tooltip>
                                                        )}
                                                    </TableCell>
                                                    <TableCell align="center" sx={{ py: 0.5 }}>
                                                        {(tx.documentUrl || tx.filepath) ? (
                                                            <Tooltip title="Supporting document attached">
                                                                <IconButton size="small" color="primary" onClick={() => { handleEditClick(tx); setShowDocPicker(true); }}><AttachmentIcon fontSize="inherit" /></IconButton>
                                                            </Tooltip>
                                                        ) : null}
                                                    </TableCell>
                                                    {/* 🎯 FIX: Bank Account cell was nested incorrectly. Moved it to be its own column. */}
                                                    <TableCell>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {(bankAccounts.find(acc => String(acc.id) === String(tx.bankAccountId)) || {}).bankName || 'N/A'}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>{formatDateForInput(tx.transactionDate)}</TableCell>
                                                    <TableCell>{tx.transactionType}</TableCell>
                                                    <TableCell>{tx.paymentReference}</TableCell>
                                                    <TableCell>{tx.statementNumber || '-'}</TableCell>
                                                    <TableCell>
                                                        {tx.transactionParty ? (
                                                            <span>{tx.transactionParty} – {tx.description || ''}</span>
                                                        ) : (
                                                            tx.description || '-'
                                                        )}
                                                    </TableCell> 
                                                    <TableCell>{tx.vatApplicable ? 'Yes' : 'No'}</TableCell>
                                                    <TableCell align="right">R {parseFloat(tx.debitTransaction || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</TableCell>
                                                    <TableCell align="right">R {parseFloat(tx.creditTransaction || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                                        R {parseFloat(tx.runningBalance).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Stack direction="row" spacing={1} justifyContent="center">
                                                            <IconButton size="small" color="primary" onClick={() => handleEditClick(tx)}><EditIcon fontSize="inherit"/></IconButton>
                                                            <IconButton size="small" color="error" onClick={async () => { if(window.confirm("Delete?")) { await axios.delete(`/api/transaction/${tx.id}`); fetchTransactions(); } }}><DeleteIcon fontSize="inherit"/></IconButton>
                                                        </Stack>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </React.Fragment>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            </Grid>
            
            {/* 0. ESTATE BANK ACCOUNTS */}
            <Grid item xs={12}>
                <Paper elevation={3} sx={{ p: 3, borderRadius: 2, mb: 1, borderTop: '6px solid #2e7d32' }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AccountBalanceIcon /> Estate Bank Accounts
                    </Typography>
                    
                    {/* Capture Form */}
                    <Box sx={{ bgcolor: '#fcfcfc', p: 2, borderRadius: 1, border: '1px solid #eee', mb: 3 }}>
                        <Typography variant="subtitle2" sx={{ mb: 2 }}>{editingBankAccountId ? 'Edit Bank Account' : 'Add Bank Account'}</Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={3}>
                                <TextField fullWidth size="small" label="Bank Name" value={bankAccountForm.bankName} onChange={(e) => setBankAccountForm(prev => ({ ...prev, bankName: e.target.value }))} disabled={user?.role === 'guest'} />
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <TextField fullWidth size="small" label="Account Number" value={bankAccountForm.bankAccountNo} onChange={(e) => setBankAccountForm(prev => ({ ...prev, bankAccountNo: e.target.value }))} disabled={user?.role === 'guest'}  />
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <TextField fullWidth size="small" type="date" label="Date Opened" InputLabelProps={{ shrink: true }} value={formatDateForInput(bankAccountForm.bankAccountOpenedDate)} onChange={(e) => setBankAccountForm(prev => ({ ...prev, bankAccountOpenedDate: e.target.value }))} disabled={user?.role === 'guest'} inputProps={{ max: '9999-12-31' }} />
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <TextField fullWidth size="small" type="date" label="Date Closed" InputLabelProps={{ shrink: true }} value={formatDateForInput(bankAccountForm.bankAccountClosedDate)} onChange={(e) => setBankAccountForm(prev => ({ ...prev, bankAccountClosedDate: e.target.value }))} disabled={user?.role === 'guest'} inputProps={{ max: '9999-12-31' }} />
                            </Grid>

                            {/* 🎯 Document Selection Inputs */}
                            <Grid item xs={12} sm={6}>
                                <Button 
                                    fullWidth variant="outlined" 
                                    size="small"
                                    color={bankAccountForm.bankAccountConfirmationUrl ? "success" : "primary"} 
                                    startIcon={bankAccountForm.bankAccountConfirmationUrl ? <CheckCircleIcon /> : <DescriptionIcon />} 
                                    onClick={() => { setDocPickerTarget('confirmationLetter'); setShowDocPicker(true); }}
                                    disabled={user?.role === 'guest'}
                                    sx={{ height: '38px', justifyContent: 'flex-start', px: 2 }}
                                >
                                    <Box sx={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                                        {bankAccountForm.bankAccountConfirmationUrl ? "Confirmation Letter Attached" : "Link Account Confirmation Letter"}
                                    </Box>
                                </Button>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Button 
                                    fullWidth variant="outlined" 
                                    size="small"
                                    color={bankAccountForm.bankAccountClosedDate ? (bankAccountForm.zeroBalanceStatementUrl ? "success" : "warning") : "primary"}
                                    startIcon={bankAccountForm.zeroBalanceStatementUrl ? <CheckCircleIcon /> : <DescriptionIcon />} 
                                    onClick={() => { setDocPickerTarget('zeroBalance'); setShowDocPicker(true); }}
                                    disabled={user?.role === 'guest'}
                                    sx={{ height: '38px', justifyContent: 'flex-start', px: 2 }}
                                >
                                    <Box sx={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                                        {bankAccountForm.zeroBalanceStatementUrl ? "Zero Balance Statement Attached" : "Link Zero Balance Statement"}
                                    </Box>
                                </Button>
                            </Grid>

                            <Grid item xs={12} sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 1 }}>
                                {editingBankAccountId && <Button variant="outlined" color="error" onClick={resetBankAccountForm}>Cancel</Button>}
                                <Button 
                                    variant="contained" color={editingBankAccountId ? "warning" : "primary"} onClick={handleSaveBankAccount}
                                    disabled={!bankAccountForm.bankName || !bankAccountForm.bankAccountNo || user?.role === 'guest'}
                                    startIcon={!editingBankAccountId && <AddCircleOutlineIcon />}
                                    sx={{ minWidth: '120px' }}
                                >
                                    {editingBankAccountId ? 'Update' : 'Add'}
                                </Button>
                            </Grid>
                        </Grid>
                    </Box>

                    {/* Table View */}
                    <TableContainer variant="outlined" component={Paper}>
                        <Table size="small">
                            <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Bank Name</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Account Number</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Date Opened</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Date Closed</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Docs</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {bankAccounts.map((acc) => (
                                    <TableRow key={acc.id} sx={{ bgcolor: acc.bankAccountClosedDate ? '#fafafa' : 'inherit', opacity: acc.bankAccountClosedDate ? 0.7 : 1 }}>
                                        <TableCell>{acc.bankName}</TableCell>
                                        <TableCell>{acc.bankAccountNo}</TableCell>
                                        <TableCell>{formatDateForInput(acc.bankAccountOpenedDate) || '-'}</TableCell>
                                        <TableCell>{formatDateForInput(acc.bankAccountClosedDate) || '-'}</TableCell>
                                        
                                        {/* 🎯 Interactive Attached Document Inline Reference Cell */}
                                        <TableCell align="center">
                                            <Stack direction="row" spacing={0.5} justifyContent="center">
                                                {acc.bankAccountConfirmationUrl ? (
                                                    <Tooltip title="View Confirmation Letter">
                                                        <IconButton 
                                                            size="small" 
                                                            color="success" 
                                                            onClick={() => {
                                                                setDocPickerTarget('confirmationLetter');
                                                                setBankAccountForm(acc);
                                                                setShowDocPicker(true);
                                                            }}
                                                        >
                                                            <DescriptionIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                ) : (
                                                    <Typography variant="caption" color="text.disabled" sx={{ px: 0.5 }}>-</Typography>
                                                )}
                                                {acc.zeroBalanceStatementUrl ? (
                                                    <Tooltip title="View Zero Balance Statement">
                                                        <IconButton 
                                                            size="small" 
                                                            color="info" 
                                                            onClick={() => {
                                                                setDocPickerTarget('zeroBalance');
                                                                setBankAccountForm(acc);
                                                                setShowDocPicker(true);
                                                            }}
                                                        >
                                                            <CheckCircleIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                ) : (
                                                    acc.bankAccountClosedDate && <Typography variant="caption" color="warning.main" sx={{ fontWeight: 'bold', px: 0.5 }}>!</Typography>
                                                )}
                                            </Stack>
                                        </TableCell>

                                        <TableCell align="center">
                                            <Stack direction="row" spacing={1} justifyContent="center">
                                                <IconButton size="small" color="primary" onClick={() => { setEditingBankAccountId(acc.id); setBankAccountForm(acc); }} disabled={user?.role === 'guest'}><EditIcon fontSize="small" /></IconButton>
                                                <IconButton size="small" color="error" onClick={() => handleDeleteBankAccount(acc.id)} disabled={user?.role === 'guest'}><DeleteIcon fontSize="small" /></IconButton>
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {bankAccounts.length === 0 && (
                                    <TableRow><TableCell colSpan={6} align="center" sx={{ py: 3, color: 'text.secondary', fontStyle: 'italic' }}>No bank accounts recorded.</TableCell></TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            </Grid>

           <Snackbar open={openSnackbar} autoHideDuration={4000} onClose={() => setOpenSnackbar(false)}>
                <Alert severity={snackbarSeverity} sx={{ width: '100%' }}>{snackbarMessage}</Alert>
            </Snackbar>
            <Dialog open={showLedgerDialog} onClose={() => setShowLedgerDialog(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ textAlign: 'center' }}>Select Ledger</DialogTitle>
                <DialogContent><Stack spacing={2} sx={{ mt: 1 }}>{LEDGER_CATEGORIES.map((cat) => (<Button key={cat.id} variant="contained" fullWidth sx={{ bgcolor: cat.color }} onClick={() => handleFinalPost(cat.id)}>{cat.label}</Button>))}</Stack></DialogContent>
            </Dialog>
            </>)}
        </Grid>
    );
};

export default CashbookTransactionsTab;